// src/lib/forfeoHandoff.ts

/**
 * Forfeo Explore handoff helpers.
 *
 * Vision:
 * - Forfeo Explore = catalogue / inspiration / SEO / découverte.
 * - Forfeo.com = moteur officiel de réservation, paiement et pass.
 * - App mobile = wallet, QR, usage terrain.
 *
 * Aucun appel réseau ici. Ce fichier construit seulement des URLs sûres.
 */

export type ForfeoHandoffTarget =
  | 'experience'
  | 'reservation'
  | 'checkout'
  | 'last_call'
  | 'gift_card'
  | 'wallet'
  | 'support';

export type ForfeoHandoffSource =
  | 'forfeo_explore'
  | 'catalogue'
  | 'boutique'
  | 'boutique_detail'
  | 'last_call'
  | 'reservation'
  | 'unknown';

export type ForfeoHandoffInput = {
  target: ForfeoHandoffTarget;
  id?: string | number | null;
  slug?: string | null;
  source?: ForfeoHandoffSource;
  title?: string | null;
  region?: string | null;
  partnerId?: string | number | null;
  fallbackPath?: string;
};

export const FORFEO_MAIN_ORIGIN = 'https://forfeo.com';
export const FORFEO_EXPLORE_ORIGIN = 'https://forfeoexplore.com';
export const FORFEO_MOBILE_SCHEME = 'forfeo://';

function safeText(value?: string | number | null) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function safePathSegment(value?: string | number | null) {
  return encodeURIComponent(safeText(value));
}

function compactParams(params: Record<string, string | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const out = search.toString();
  return out ? `?${out}` : '';
}

export function buildForfeoReservationUrl(input: ForfeoHandoffInput): string {
  const idOrSlug = safePathSegment(input.slug || input.id || '');
  const source = input.source || 'forfeo_explore';
  const qs = compactParams({
    source,
    title: input.title ? safeText(input.title) : undefined,
    region: input.region ? safeText(input.region) : undefined,
    partnerId: input.partnerId ? safeText(input.partnerId) : undefined,
  });

  if (input.target === 'last_call') {
    return `${FORFEO_MAIN_ORIGIN}/reserver/last-call/${idOrSlug}${qs}`;
  }

  if (input.target === 'checkout') {
    return `${FORFEO_MAIN_ORIGIN}/checkout/experience/${idOrSlug}${qs}`;
  }

  if (input.target === 'gift_card') {
    return `${FORFEO_MAIN_ORIGIN}/carte-cadeau${qs}`;
  }

  if (input.target === 'wallet') {
    return `${FORFEO_MAIN_ORIGIN}/wallet${qs}`;
  }

  if (input.target === 'support') {
    return `${FORFEO_MAIN_ORIGIN}/contact${qs}`;
  }

  return `${FORFEO_MAIN_ORIGIN}/reserver/${idOrSlug}${qs}`;
}

export function buildForfeoExperienceUrl(input: ForfeoHandoffInput): string {
  const idOrSlug = safePathSegment(input.slug || input.id || '');
  const qs = compactParams({
    source: input.source || 'forfeo_explore',
    title: input.title ? safeText(input.title) : undefined,
    region: input.region ? safeText(input.region) : undefined,
  });
  return `${FORFEO_MAIN_ORIGIN}/experience/${idOrSlug}${qs}`;
}

export function buildForfeoMobileHandoffUrl(input: ForfeoHandoffInput): string {
  const idOrSlug = safePathSegment(input.slug || input.id || '');
  const path = input.target === 'last_call'
    ? `last-call/${idOrSlug}`
    : input.target === 'wallet'
      ? 'wallet'
      : `experience/${idOrSlug}`;

  const qs = compactParams({
    source: input.source || 'forfeo_explore',
    title: input.title ? safeText(input.title) : undefined,
    region: input.region ? safeText(input.region) : undefined,
  });

  return `${FORFEO_MOBILE_SCHEME}${path}${qs}`;
}

export function openForfeoReservation(input: ForfeoHandoffInput) {
  if (typeof window === 'undefined') return;
  window.location.href = buildForfeoReservationUrl(input);
}

export function openForfeoMobileOrWeb(input: ForfeoHandoffInput) {
  if (typeof window === 'undefined') return;

  const mobileUrl = buildForfeoMobileHandoffUrl(input);
  const webUrl = buildForfeoReservationUrl(input);
  const startedAt = Date.now();

  window.location.href = mobileUrl;

  window.setTimeout(() => {
    if (Date.now() - startedAt < 1800) {
      window.location.href = webUrl;
    }
  }, 900);
}
